/* ==========================================================================
   MOMANI — variant-dependent surfaces, the size-guide drawer, and the
   mobile persistent buy bar's contents.
   ==========================================================================

   ── WHY THIS FILE EXISTS AT ALL ──────────────────────────────────────────
   product-info.js re-fetches the product section through the Section
   Rendering API on every variant change, then applies a WHITELIST: it swaps
   #price, #Sku, #Inventory, #Volume, #Price-Per-Item, the whole
   <variant-selects> element, the media gallery, quantity rules, and the
   submit button's disabled state. Anything else silently fails to update.

   For MOMANI that list is real: the sold-out replacement of the buy block,
   the "Also available in {size}" link, and the persistent bar's price and
   control all live outside it.

   The obvious fix — adding ids to product-info.js's whitelist — is the wrong
   one. That file is the most correctness-critical code in the theme and the
   most expensive to get wrong, and editing it forks a Dawn file that a
   future upgrade has to merge by hand.

   So this component SUBSCRIBES to PUB_SUB_EVENTS.variantChange instead. The
   event hands it `html` — the freshly fetched section document — and every
   region here is REFILLED from that document rather than reconstructed, so
   the markup has exactly one source (the Liquid) and the two sold-out
   states stay distinguishable. product-info.js stays at stock. That is
   dawn_architecture_audit.md §4's standing decision and gate G25's evidence.

   P4 and P5 need nothing here: the colour-name line and the size chips live
   INSIDE <variant-selects>, which Dawn replaces wholesale.

   ── THE BUY BAR HAS ONE OWNER ────────────────────────────────────────────
   momani-ui.js shows and hides [data-momani-buy-bar] from scroll position
   (class is-visible, once the in-page control has scrolled above the
   viewport). This file never toggles that class: it only lifts the bar's
   no-JS `hidden` attribute once, and refills the bar's contents on a
   variant change. An earlier IntersectionObserver copy of the show/hide
   logic lived here and showed the bar from page load whenever the buy
   region sat below the fold; it is gone.

   ── MOTION ───────────────────────────────────────────────────────────────
   No tweening happens in JavaScript. This file toggles classes and the
   `hidden` attribute; every transition is CSS, token-driven, and gated by
   prefers-reduced-motion in momani-motion.css.
   ========================================================================== */

(function () {
  'use strict';

  const SELECTORS = {
    buyAux: '[data-momani-buy-aux]',
    bar: '[data-momani-buy-bar]',
    barPrice: '[data-momani-bar-price]',
    notifyTrigger: '[data-momani-notify-trigger]',
    notifyPanel: '[data-momani-notify-panel]',
    nearestSize: '[data-momani-nearest-size]',
    guideTrigger: '[data-momani-size-guide-trigger]',
    guide: '[data-momani-size-guide]',
    guideClose: '[data-momani-size-guide-close]',
    optionInput: '.momani-option__input',
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Variant-dependent regions outside product-info.js's whitelist ────── */
  function syncVariantSurfaces(detail) {
    const data = detail && detail.data;
    if (!data) return;

    const { html, sectionId } = data;
    const root = document.getElementById(`MainProduct-${sectionId}`);
    if (!root || !html) return;

    // MOMANI's variant-dependent extras — the notify control, the restock
    // line and the nearest-size route.
    //
    // SCOPED TO THE AUX REGION ON PURPOSE. The obvious version of this swaps
    // the whole buy region, which is wrong in a way that takes a while to
    // see: that region contains Dawn's submit button, and product-form.js
    // caches that element ONCE, in its constructor. Replacing the region's
    // innerHTML detaches it; Dawn then disables, labels and un-disables a
    // node that is no longer in the document. Dawn toggles that button
    // itself, so it only needs to be left alone.
    const sourceAux = html.querySelector(SELECTORS.buyAux);
    const targetAux = root.querySelector(SELECTORS.buyAux);
    if (sourceAux && targetAux) targetAux.innerHTML = sourceAux.innerHTML;

    // The notify panel lives outside <product-form> (a <form> cannot nest)
    // and is rendered in every state, so this target is always present. It
    // is re-filled rather than re-created, and closed again on every variant
    // change: an open notify form carrying the previous size's hidden
    // variant fields would queue the shopper for the wrong garment.
    const sourceNotify = html.querySelector(SELECTORS.notifyPanel);
    const targetNotify = root.querySelector(SELECTORS.notifyPanel);
    if (targetNotify) {
      if (sourceNotify) targetNotify.innerHTML = sourceNotify.innerHTML;
      targetNotify.hidden = true;
    }

    // The persistent bar: its price belongs to the SELECTED variant and its
    // control mirrors the in-page state (add to bag · notify · sold out ·
    // not available). Both come from the rendered source, never assembled
    // here — money formatting is Shopify's, and the control's state has one
    // source of truth in the Liquid. The bar element itself is never
    // replaced: momani-ui.js holds a reference to it.
    const sourceBar = html.querySelector(SELECTORS.bar);
    const bar = document.querySelector(SELECTORS.bar);
    if (sourceBar && bar) bar.innerHTML = sourceBar.innerHTML;

    bindBuyRegion();
  }

  /* ── The notify control and the nearest-size route ────────────────────── */
  function bindBuyRegion() {
    document.querySelectorAll(SELECTORS.notifyTrigger).forEach((trigger) => {
      if (trigger.dataset.momaniBound === 'true') return;
      trigger.dataset.momaniBound = 'true';
      trigger.addEventListener('click', () => {
        const panel = document.querySelector(SELECTORS.notifyPanel);
        if (!panel) return;
        panel.hidden = false;
        const field = panel.querySelector('input[type="email"]');
        if (field) field.focus();
      });
    });

    // "Also available in {size}" selects that size's input rather than
    // navigating, so the shopper stays on the page and the variant change
    // runs through Dawn's own path. The size to select is carried on the
    // control (data-momani-nearest-size), never parsed out of its label.
    document.querySelectorAll(SELECTORS.nearestSize).forEach((link) => {
      if (link.dataset.momaniBound === 'true') return;
      link.dataset.momaniBound = 'true';
      link.addEventListener('click', () => {
        const wanted = link.dataset.momaniNearestSize;
        if (!wanted) return;
        const inputs = [...document.querySelectorAll(SELECTORS.optionInput)].filter((input) => !/^colou?r$/i.test(input.dataset.optionName || ''));
        const hit = inputs.find((input) => input.value === wanted && input.dataset.variantState !== 'unavailable');
        if (!hit) return;
        hit.checked = true;
        hit.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ── Size guide drawer — focus moved in, trapped, and RETURNED on close ──
     The drawer sits outside the sticky information column (main-product
     renders it after the buy bar) so it paints above the header. Its
     trigger lives inside <variant-selects>, which Dawn replaces on every
     variant change, so the trigger is handled by delegation; the drawer
     itself is bound once.                                                   */
  function bindSizeGuide() {
    const guide = document.querySelector(SELECTORS.guide);
    if (!guide) return;

    if (guide.dataset.momaniBound !== 'true') {
      guide.dataset.momaniBound = 'true';

      let lastFocused = null;

      const onOutside = (event) => {
        if (guide.contains(event.target) || event.target.closest(SELECTORS.guideTrigger)) return;
        close();
      };

      const close = () => {
        if (guide.hidden) return;
        guide.classList.remove('is-open');
        guide.hidden = true;
        document.removeEventListener('pointerdown', onOutside, true);
        // Returning focus to the SIZE CONTROL is the floor requirement, and
        // it is the half of a drawer that is most often missed.
        if (lastFocused && lastFocused.isConnected) lastFocused.focus();
      };

      const open = () => {
        if (!guide.hidden) return;
        lastFocused = document.activeElement;
        guide.hidden = false;
        // A frame so the class lands after `hidden` is removed, otherwise the
        // transition has nothing to animate from. Focus is moved only once
        // the drawer is visible: focusing a visibility:hidden control is a
        // silent no-op, which is how Escape once stopped working here.
        requestAnimationFrame(() => {
          guide.classList.add('is-open');
          requestAnimationFrame(() => {
            const closeButton = guide.querySelector(SELECTORS.guideClose);
            (closeButton || guide).focus();
          });
        });
        setTimeout(() => document.addEventListener('pointerdown', onOutside, true), 0);
      };

      guide.__momaniOpen = open;

      guide.querySelectorAll(SELECTORS.guideClose).forEach((button) => button.addEventListener('click', close));

      // Escape closes the drawer wherever focus is, so a shopper who opened
      // it with a pointer and never tabbed in is not stranded.
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || guide.hidden) return;
        event.stopPropagation();
        close();
      }, true);

      guide.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = guide.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    // The trigger is delegated: <variant-selects> is replaced on every
    // variant change, and Dawn publishes variantChange before that swap
    // lands, so a listener bound to the trigger element would be bound to
    // the one about to disappear.
    if (document.body.dataset.momaniGuideDelegated !== 'true') {
      document.body.dataset.momaniGuideDelegated = 'true';
      document.addEventListener('click', (event) => {
        const hit = event.target.closest(SELECTORS.guideTrigger);
        if (!hit) return;
        const drawer = document.querySelector(SELECTORS.guide);
        if (drawer && drawer.__momaniOpen) drawer.__momaniOpen();
      });
    }
  }

  /* ── Persistent buy bar — lift the no-JS `hidden` once ──────────────────
     With scripting off the bar stays hidden and the in-page control is the
     only one. With scripting on, momani-ui.js reveals it from scroll
     position; a disabled control (sold out, not available) keeps the bar
     hidden through CSS.                                                    */
  function liftBuyBar() {
    const bar = document.querySelector(SELECTORS.bar);
    if (bar) bar.hidden = false;
  }

  function init() {
    bindBuyRegion();
    bindSizeGuide();
    liftBuyBar();
  }

  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState !== 'loading') init();

  // PUB_SUB_EVENTS and subscribe() are Dawn's, loaded globally from
  // constants.js and pubsub.js. This is a subscriber, not a replacement.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      syncVariantSurfaces(event);
      bindSizeGuide();
    });
  }

  // Exposed only so a reduced-motion check has one source if it is ever
  // needed from markup; nothing in this file tweens.
  window.momaniPrefersReducedMotion = prefersReducedMotion;
})();
